import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { SENTRY_DSN, ENV, IS_BOT, SENTRY_RELEASE } from './constants';
import {
  getReduxContext,
  sanitizeReduxContext,
  sanitizeState,
  stripReduxStateAttachment,
} from './utils/sentryStateSanitization';

const sanitizeKeys = [
  /ssn/i,
  /phone/i,
  /dob/i,
  /driverlicense/i,
  /motherMaidenName/i,
  /i18n/i,
];

const ignoreErrors = [
  // Error generated by a bug in auto-fill library from browser
  // https://github.com/getsentry/sentry/issues/5267
  /Blocked a frame with origin "https?:\/\/candidate\.checkr\.com" from accessing/,
  'invalid token',
  'no candidate found',
  'Bad authentication error',
  /Failed to execute 'createElement' on 'Document'/,
  'https://privacyportal.onetrust.com/request/v1/consentreceipts',
  /Timeout \(.\)/,
  "TypeError: undefined is not an object (evaluating 'window.webkit.messageHandlers.selectedDebugHandler.postMessage')",
  // High-volume, low-signal errors that inflate replay recording (INC-4731)
  'Must pass the zipcode as a string',
  'Must pass a 5-digit numeric zipcode',
  /Amplitude Logger \[Error\]/,
  /csp_violation/,
];

if (IS_BOT) {
  const ignoreBot = /Invalid captcha/;
  ignoreErrors.push(ignoreBot);
}

const paths = [
  /https?:\/\/candidate\.checkr\.com/,
  /https?:\/\/applicant-portal\.checkrhq-staging\.net/,
  /https?:\/\/candidate\.checkrhq-staging\.net/,
  /https?:\/\/candidate\.checkr\.localhost/,
];

// team tags
const tags = {
  team: 'dash_experience',
};

const filteredReduxActions = ['@@i18n/LOAD_TRANSLATIONS'];

// Redux scrubbing helpers live in `./utils/sentryStateSanitization` so
// they can be unit-tested directly — `sentry.js` itself is in
// `coveragePathIgnorePatterns` because most of it is the side-effectful
// `Sentry.init` call.

const initializeSentry = () => {
  if (ENV === 'development' || !SENTRY_RELEASE) return;
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENV,
    release: SENTRY_RELEASE,
    // normalize context depth
    normalizeDepth: 12,
    ignoreErrors,
    allowUrls: [/checkr\.com/, /checkrhq-staging\.net/, /checkr\.localhost/],
    // Low sampling rate keeps the routing-aware integration active (so route
    // patterns like `/reports/:reportId` get attached to errors) without
    // generating significant tracing volume.
    tracesSampleRate: 0.01,
    traceLifecycle: 'static',
    // Candidate data must not leave the browser. The SDK's defaults collect
    // all of these, so each is turned off explicitly. User-Agent stays so
    // Sentry can still derive browser and OS tags.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: { allow: ['user-agent'] }, response: false },
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
    },
    integrations: [
      Sentry.rewriteFramesIntegration({
        iteratee: frame => {
          const fr = frame;
          if (fr.filename) {
            for (const path of paths) {
              if (path.test(fr.filename)) {
                fr.in_app = true;
              }
            }
          }
          return fr;
        },
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // clean up redux state from sensitive information
    beforeSend(event, hint) {
      // https://github.com/getsentry/sentry-javascript/issues/2514#issuecomment-603971338
      if (hint?.originalException === 'Timeout') return null;

      // The Redux enhancer is configured with `attachReduxState: false`, but
      // drop any raw `redux_state.json` attachment here too so unscrubbed
      // store state can never leave the browser — on both the success and
      // the catch path below.
      stripReduxStateAttachment(hint);

      try {
        const breadcrumbs = (event.breadcrumbs || []).filter(
          breadcrumb => !filteredReduxActions.includes(breadcrumb.data?.type),
        );
        sanitizeState(breadcrumbs, sanitizeKeys);

        const reduxLookup = getReduxContext(event?.contexts);
        if (!reduxLookup) {
          return { ...event, breadcrumbs };
        }

        const safeReduxState = sanitizeReduxContext(
          reduxLookup.value,
          sanitizeKeys,
        );

        return {
          ...event,
          breadcrumbs,
          contexts: {
            ...event?.contexts,
            [reduxLookup.key]: safeReduxState,
          },
        };
      } catch {
        const contexts = { ...event?.contexts };
        delete contexts.state;
        delete contexts['redux.state'];
        return { ...event, breadcrumbs: [], contexts };
      }
    },
    beforeBreadcrumb(breadcrumb) {
      // filter non console error events due to size limitations in breadcrumbs
      if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
        return null;
      }
      return breadcrumb;
    },
  });

  Object.keys(tags).forEach(tag => {
    Sentry.setTag(tag, tags[tag]);
  });
};

export default initializeSentry;
